/**
 * high-striker.js — GDD 6.6.1. The strongman tower.
 *
 * The player picks up a heavy mallet, swings it overhead and brings it down on
 * a lever pad at waist height. The puck flies up a 2.2 m tower through five
 * zones — Weakling 50, Average 100, Strong 250, Mighty 500 — and rings the bell
 * for 1000 above 4 m/s. Three swings; the round scores the BEST of them.
 *
 * The load-bearing rule (GDD 6.6.1 "Y-axis only ... prevents cheese"): only the
 * DOWNWARD component of the mallet-head velocity counts. A fast sideways swipe
 * across the pad registers as a swing and scores zero. That is why this stall
 * tracks the head's own world pose in a ring buffer rather than reading the
 * hand or the rigid body — the head is what strikes the pad, so its motion is
 * the only thing that may be read as power.
 *
 * The mallet-head is deliberately NOT a throwable. It is spawned and registered
 * with the grab system directly, so releasing it costs nothing; attempts are
 * consumed by pad strikes alone.
 *
 * Geometry lives in `high-striker-parts.js`.
 *
 * @module stalls/high-striker
 */

import * as THREE from 'three';
import { HIGH_STRIKER, EVENTS } from '../constants.js';
import { StallBase, registerStall } from './framework.js';
import * as factory from '../assets3d/factory.js';
import * as parts from './high-striker-parts.js';
import { HeadTracker } from './head-tracker.js';
import { grab } from '../core/grab.js';
import { bus } from '../core/events.js';
import { createStallLight } from '../world/lighting.js';
import { haptics } from '../core/haptics.js';

const CFG = HIGH_STRIKER;

/** Bottom and top of the puck's travel, in local Y. */
const PUCK_BASE_Y = 0.36;
const PUCK_TOP_Y = CFG.towerHeight - 0.06;
/** Head radius of the high-striker mallet, from the factory's dimensions. */
const HEAD_RADIUS = 0.09;

class HighStriker extends StallBase {
  constructor(ctx) {
    super(ctx);
    this.mallet = null;
    this.puck = null;
    this.bell = null;
    this.bellHandle = null;
    this.pad = null;
    /** @type {Array<{ def: object, mesh: THREE.Object3D, handle: object }>} */
    this.zonePlates = [];

    this._head = new HeadTracker({
      bufferSize: CFG.velocityBufferSize,
      smoothFrames: CFG.velocitySmoothFrames,
      weightDecay: CFG.velocityWeightDecay,
    });
    this._headClear = true;
    this._strikeCooldown = 0;
    this._elapsed = 0;
    this._settleTimer = 0;

    this.bestZone = null;
    this.lastZone = null;
    this.lastVelocity = 0;
    this._puckAnim = null;
    this._bellFlash = 0;
    this._padPress = 0;
    this._headOffset = new THREE.Vector3(0, 0.95, 0);
  }

  // ── Static build ────────────────────────────────────────────────────────

  onBuild() {
    const floor = parts.createFloorSlab();
    this.spawn(floor, { x: 0, y: -0.05, z: 1.4 }, {
      type: 'fixed', shape: 'box', halfExtents: { x: 1.8, y: 0.05, z: 1.8 },
      position: this.localToWorld(0, -0.05, 1.4),
      friction: 0.9, restitution: 0.05,
    });

    this._buildTower();
    this._buildPad();
    // On the player's LEFT (+x), because the parked mallet at x −0.3, z 0.5
    // stands camera-RIGHT and close to the lens: its shaft covers a fixed
    // bearing on that side that no board can dodge by moving further right or
    // deeper, and clearing the head vertically would need y > 4. Mirroring to
    // +x uses the empty upper-left quadrant; the zone labels are the only other
    // thing there and they sit tight to the tower at 2.85 m. z 1.95 keeps the
    // outer edge on the same bearing it had at (−1.1, 1.7), so edge framing is
    // unchanged at any aspect — only the side changed.
    this.buildScoreboard({ x: 1.35, y: 1.85, z: 1.95, width: 1.2, height: 0.8 });

    // Splits the difference between the pad at 0.86 and the tower at 3.0.
    const light = createStallLight(0xffe0b0, 2.2, 10);
    light.position.set(0, 2.9, 2.0);
    this.root.add(light);
  }

  /** @private */
  _buildTower() {
    const column = parts.createColumn();
    this.spawn(column, { x: 0, y: CFG.towerHeight / 2, z: CFG.towerZ }, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: CFG.towerWidth / 2, y: CFG.towerHeight / 2, z: 0.11 },
      position: this.localToWorld(0, CFG.towerHeight / 2, CFG.towerZ),
    });

    const plateZ = CFG.towerZ - 0.13;
    const span = PUCK_TOP_Y - PUCK_BASE_Y;
    this.zonePlates = CFG.zones.map((def, i) => {
      const y = PUCK_BASE_Y + (span * (i + 0.62)) / CFG.zones.length;
      const mesh = parts.createZonePlate(def.color);
      const desc = {
        type: 'fixed',
        shape: 'box',
        halfExtents: { x: CFG.towerWidth * 0.46, y: 0.028, z: 0.015 },
        position: this.localToWorld(0, y, plateZ),
      };
      const handle = this.spawn(mesh, { x: 0, y, z: plateZ }, desc);
      handle.userData.zone = def.key;
      handle.userData.points = def.points;

      const label = factory.createSign('', {
        w: 0.68, h: 0.15,
        lines: [`${def.name}  ${def.points}`],
        bg: '#12172a', color: def.color,
      });
      label.position.set(CFG.towerWidth / 2 + 0.38, y, plateZ - 0.02);
      label.rotation.y = Math.PI;
      this.root.add(label);

      return { def, mesh, handle };
    });

    // The puck rides in front of the plates so it is never occluded by them.
    this.puck = this.spawn(parts.createPuck(), { x: 0, y: PUCK_BASE_Y, z: CFG.towerZ - 0.2 }, {
      type: 'kinematic', shape: 'box',
      halfExtents: { x: CFG.towerWidth * 0.36, y: 0.05, z: 0.045 },
      position: this.localToWorld(0, PUCK_BASE_Y, CFG.towerZ - 0.2),
    });

    this.bell = parts.createBell();
    this.bellHandle = this.spawn(this.bell, { x: 0, y: CFG.bellY, z: CFG.towerZ - 0.05 }, {
      type: 'fixed', shape: 'ball', radius: 0.13,
      position: this.localToWorld(0, CFG.bellY, CFG.towerZ - 0.05),
    });
  }

  /** @private */
  _buildPad() {
    this.pad = parts.createLeverPad();
    this.spawn(this.pad, { x: 0, y: CFG.padY, z: CFG.padZ }, {
      type: 'fixed', shape: 'cylinder',
      radius: CFG.padRadius, halfHeight: CFG.padThickness / 2,
      position: this.localToWorld(0, CFG.padY, CFG.padZ),
      friction: 0.9, restitution: 0.05,
    });
    this._padRestY = CFG.padY;
  }

  // ── Round setup ─────────────────────────────────────────────────────────

  setupRound() {
    this.setRoundFormat({ attempts: CFG.swings });
    this.bestZone = null;
    this.lastZone = null;
    this.lastVelocity = 0;
    this._head.reset();
    this._headClear = true;
    this._strikeCooldown = 0;
    this._elapsed = 0;
    this._settleTimer = 0;
    this._puckAnim = null;
    this._bellFlash = 0;
    this._padPress = 0;

    // Zone plates and the bell are the scoring objects, so they are what a test
    // sees through `__cc.stalls.targets()`. They are built once but registered
    // every round, because `_beginRound` clears the target list.
    for (const plate of this.zonePlates) {
      this.registerTarget(plate.handle, { id: `zone-${plate.def.key}`, group: 'zone' });
    }
    if (this.bellHandle) this.registerTarget(this.bellHandle, { id: 'bell', group: 'bell' });

    this._spawnMallet();
    this._setPuckHeight(PUCK_BASE_Y);
  }

  /**
   * The mallet: grabbable, but not a throwable. Releasing it must not consume a
   * swing, so it never goes through `spawnThrowable`.
   * @private
   */
  _spawnMallet() {
    const mesh = factory.createMallet('high-striker');
    const rest = CFG.malletRest;
    this.mallet = this.spawn(mesh, rest, {
      position: this.localToWorld(rest.x, rest.y, rest.z),
      linearDamping: 0.2, angularDamping: 0.4,
    });
    this.mallet.userData.mallet = true;
    this._malletRestWorld = this.mallet.position().clone();
    this._headOffset = (mesh.userData.headOffset || new THREE.Vector3(0, 0.95, 0)).clone();

    grab.register(this.mallet, {
      kind: 'mallet',
      holdOffset: mesh.userData.holdOffset || null,
      onGrab: () => {
        this.mallet.userData.parked = false;
        haptics.play('right', 'grab');
      },
    });
    this._parkMallet();
  }

  /**
   * Hold the mallet still at its rest pose.
   *
   * A 3.5 kg dynamic body spawned at grab height has nothing under it and would
   * simply fall, and a mallet standing on the end of its handle topples anyway.
   * Parking it kinematically is what makes it wait to be picked up. `grab` puts
   * it back to dynamic on release, so a real swing is unaffected.
   * @private
   */
  _parkMallet() {
    if (!this.mallet || this.mallet.removed) return;
    this.mallet.setPosition(this._malletRestWorld);
    this.mallet.setRotation(this.worldQuaternion());
    this.mallet.freeze();
    this.mallet.setKinematic(true);
    this.mallet.userData.parked = true;
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  onUpdate(dt) {
    this._elapsed += dt;
    this._strikeCooldown = Math.max(0, this._strikeCooldown - dt);

    this._trackHead();
    this._animatePuck(dt);
    this._animateFeedback(dt);
    this._leashMallet();
    this._checkRoundEnd(dt);
  }

  /**
   * Sample the mallet-head world pose and test it against the pad.
   * @private
   */
  _trackHead() {
    if (!this.mallet || this.mallet.removed || !this.mallet.mesh) return;
    const mesh = this.mallet.mesh;
    mesh.updateWorldMatrix(true, false);
    const headWorld = new THREE.Vector3();
    headWorld.copy(this._headOffset).applyMatrix4(mesh.matrixWorld);

    this._head.push(headWorld, this._elapsed);

    const local = this.worldToLocal(headWorld);
    const padTop = CFG.padY + CFG.padThickness / 2 + HEAD_RADIUS;
    const overPad = Math.hypot(local.x, local.z - CFG.padZ) < CFG.padRadius + HEAD_RADIUS;
    const touching = overPad && local.y <= padTop;

    if (touching && this._headClear && this._strikeCooldown <= 0) {
      this._registerStrike(this._head.velocity());
    }
    // Rearming needs the head clear of the pad, not merely above the plane, or
    // a mallet-head left resting on the pad re-triggers on every wobble.
    this._headClear = !touching;
  }

  /**
   * Score a pad strike. Only −Y world motion counts (GDD 6.6.1).
   * @param {THREE.Vector3} headVelocity World-space velocity of the mallet-head.
   * @returns {{ downward: number, zone: string|null, points: number }|null}
   * @private
   */
  _registerStrike(headVelocity) {
    if (!this.roundActive || this.attemptsLeft <= 0) return null;
    this._strikeCooldown = CFG.strikeCooldown;

    // Sideways motion contributes nothing. This one line is the anti-cheese
    // rule from GDD 6.6.1; everything downstream reads only this number.
    const downward = Math.max(0, -headVelocity.y);
    this.lastVelocity = downward;

    let zone = null;
    for (const def of CFG.zones) {
      if (downward > def.minVelocity) zone = def;
    }
    this.lastZone = zone;
    const points = zone ? zone.points : 0;

    if (points > this.score) {
      // The round scores the BEST of three swings (GDD 6.6.1), so only an
      // improvement is banked.
      this.addScore(points - this.score, {
        position: this.localToWorld(0, CFG.padY + 0.35, CFG.padZ),
        label: zone.name,
        color: zone.color,
      });
      this.bestZone = { key: zone.key, points, velocity: downward };
    } else {
      this.updateScoreboard();
    }

    this._padPress = 1;
    this._launchPuck(downward);
    this._markZoneReached(zone);

    bus.emit(EVENTS.AUDIO_PLAY, {
      name: zone ? 'striker-hit' : 'striker-dud',
      volume: zone ? Math.min(1, 0.5 + downward / 8) : 0.4,
    });
    haptics.playBoth('impact', { force: Math.min(30, downward * 6) });

    this.consumeAttempt();
    return { downward, zone: zone ? zone.key : null, points };
  }

  /** @private */
  _markZoneReached(zone) {
    if (!zone) return;
    const reached = CFG.zones.findIndex((z) => z.key === zone.key);
    this.zonePlates.forEach((p, i) => {
      if (i <= reached) p.handle.userData.scored = true;
    });
    if (zone.key === 'bell') this._ringBell();
  }

  /** @private */
  _ringBell() {
    this._bellFlash = 1;
    if (this.bellHandle) this.bellHandle.userData.scored = true;
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'bell', volume: 1 });
    haptics.playBoth('score');
  }

  // ── Puck and feedback ───────────────────────────────────────────────────

  /** @private */
  _launchPuck(downward) {
    const t = THREE.MathUtils.clamp(downward / CFG.puckFullScaleVelocity, 0, 1);
    this._puckAnim = {
      t: 0,
      peak: PUCK_BASE_Y + (PUCK_TOP_Y - PUCK_BASE_Y) * t,
      phase: 'rise',
    };
  }

  /** @private */
  _animatePuck(dt) {
    const a = this._puckAnim;
    if (!a) return;
    a.t += dt;

    if (a.phase === 'rise') {
      const k = Math.min(1, a.t / CFG.puckRiseTime);
      // Ease out — the puck decelerates as it climbs, like a real striker.
      this._setPuckHeight(PUCK_BASE_Y + (a.peak - PUCK_BASE_Y) * (1 - (1 - k) * (1 - k)));
      if (k >= 1) { a.phase = 'hang'; a.t = 0; }
      return;
    }
    if (a.phase === 'hang') {
      if (a.t >= CFG.puckHangTime) { a.phase = 'fall'; a.t = 0; }
      return;
    }
    const k = Math.min(1, a.t / CFG.puckFallTime);
    this._setPuckHeight(a.peak - (a.peak - PUCK_BASE_Y) * k * k);
    if (k >= 1) this._puckAnim = null;
  }

  /** @private */
  _setPuckHeight(y) {
    if (!this.puck || this.puck.removed) return;
    this.puck.setPosition(this.localToWorld(0, y, CFG.towerZ - 0.2));
  }

  /** @private */
  _animateFeedback(dt) {
    if (this._bellFlash > 0) {
      this._bellFlash = Math.max(0, this._bellFlash - dt * 1.6);
      if (this.bell) {
        this.bell.scale.setScalar(
          1 + Math.sin(this._bellFlash * Math.PI * 6) * 0.16 * this._bellFlash,
        );
      }
    } else if (this.bell) {
      this.bell.scale.setScalar(1);
    }

    if (this._padPress > 0) {
      this._padPress = Math.max(0, this._padPress - dt * 3.2);
      if (this.pad) this.pad.position.y = this._padRestY - 0.06 * this._padPress;
    }
  }

  /**
   * A dropped mallet cannot be recovered on desktop, where the hand cannot reach
   * the floor. Put it back within reach instead.
   * @private
   */
  _leashMallet() {
    if (!this.mallet || this.mallet.removed) return;
    if (this.mallet.userData.parked) return;
    if (grab.heldBy('left') === this.mallet || grab.heldBy('right') === this.mallet) return;
    const pos = this.mallet.position();
    const strayed = pos.distanceTo(this._malletRestWorld) > CFG.malletLeashRadius;
    const onFloor = this.worldToLocal(pos).y < 0.25;
    if (!strayed && !onFloor) return;
    if (this.mallet.velocity().lengthSq() > 0.25) return; // still in flight
    this._parkMallet();
  }

  /** @private */
  _checkRoundEnd(dt) {
    if (!this.roundActive || this.attemptsLeft > 0) return;
    this._settleTimer += dt;
    if (this._settleTimer >= CFG.settleTime) this.endRound();
  }

  // ── Scoreboard ──────────────────────────────────────────────────────────

  updateScoreboard() {
    if (!this.scoreboard) return;
    this.scoreboard.setLines([
      `Best    ${this.score}`,
      `Swings  ${this.attemptsLeft} / ${this.maxAttempts}`,
      `Last    ${this.lastZone ? this.lastZone.name : '—'}`,
      `Down    ${this.lastVelocity.toFixed(1)} m/s`,
    ]);
  }

  // ── Test automation ─────────────────────────────────────────────────────

  /**
   * Additive hooks reached through `__cc.stalls.action(name, args)` and the
   * `__cc.stalls.simulateStrike()` wrapper.
   *
   * `strike` injects a mallet-head velocity into `_registerStrike` — the same
   * method the geometric pad detection calls — so a test can drive every zone
   * and the sideways-swing case exactly.
   *
   * @param {string} name
   * @param {object} args
   */
  testHook(name, args = {}) {
    switch (name) {
      case 'strike': {
        const v = Array.isArray(args.velocity)
          ? new THREE.Vector3(args.velocity[0], args.velocity[1], args.velocity[2])
          : new THREE.Vector3(0, -(args.velocityY ?? 0), 0);
        this._strikeCooldown = 0;
        return this._registerStrike(v);
      }
      case 'state':
        return {
          best: this.bestZone,
          last: this.lastZone ? this.lastZone.key : null,
          lastVelocity: this.lastVelocity,
          puckY: this.puck && !this.puck.removed
            ? this.worldToLocal(this.puck.position()).y : null,
          puckBaseY: PUCK_BASE_Y,
          puckTopY: PUCK_TOP_Y,
          bellRung: !!(this.bellHandle && this.bellHandle.userData.scored),
          zones: CFG.zones.map((z) => ({
            key: z.key, points: z.points, minVelocity: z.minVelocity,
          })),
        };
      default:
        return null;
    }
  }

  onTeardown() {
    if (this.mallet) grab.unregister(this.mallet);
    this.mallet = null;
    this.puck = null;
    this.bell = null;
    this.bellHandle = null;
    this.pad = null;
    this.zonePlates = [];
    this._head.reset();
    this._puckAnim = null;
  }
}

registerStall({
  id: 'high-striker',
  name: 'High Striker',
  cost: CFG.cost,
  create: (ctx) => new HighStriker(ctx),
});

export { HighStriker };
